import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Printer } from 'lucide-react'
import { useLmsPrint } from '@/hooks/useLmsPrint'
import { LmsPdfDownloadButton } from '@/components/admin/print/LmsPdfDownloadButton'
import { coachingKeys } from '@/hooks/coaching/useCoachingToday'
import { useLearnerToday } from '@/hooks/coaching/useLearnerToday'
import { quizScriptApi } from '@/api/quizScript'
import { lcQuizSetApi } from '@/api/lcQuizSet'
import { lcReviewNoteApi } from '@/api/lcReviewNote'
import { lcCoachingApi } from '@/api/lcCoaching'
import { unitAssessmentAdminApi } from '@/api/unitAssessmentAdmin'
import { getQuizSet } from '@/api/quizSet'
import { flattenQuizIds } from '@/components/admin/diagnostics/DiagnosticSetsTab'
import {
  PrintQuizItem,
  QuizNumberColorProvider,
} from '@quebon/shared-learning'
import type { QuizScript } from '@quebon/shared-learning'
import {
  LC_PRINT_A4_HEIGHT_PX,
  LC_PRINT_FIRST_PAGE_CONTENT_TOP_PX,
  LC_PRINT_A4_WIDTH_PX,
  LC_PRINT_QUIZ_SPACING_PX,
  useWrongNotePagination,
  WRONG_NOTE_CONTINUATION_TOP_PX,
} from '@/components/admin/coaching/print/useWrongNotePagination'
import { WrongNotePrintHeader } from '@/components/admin/coaching/print/WrongNotePrintHeader'
import { WrongNotePrintFooter } from '@/components/admin/coaching/print/WrongNotePrintFooter'
import {
  formatPrintFileNameTimestamp,
  formatPrintFileNameTimestampKst,
  sanitizePrintFileNameSegment,
} from '@/utils/printFileName'
import { levelSetGradeLabelKo } from '@/utils/levelSetName'
import { getReviewNoteSourceCounts } from '@/utils/reviewNoteSources'

/** 시안(node 654:942) 문제 번호 색 — step별 색이 아닌 단일 회색. */
const QUIZ_NUMBER_COLOR = '#a5a5a5'
type PrintKind = 'practice' | 'wrong' | 'essential' | 'review-note' | 'chapter' | 'cumulative' | 'diagnostic-level' | 'unit-assessment'
type NamedTestPrintKind = 'chapter' | 'cumulative' | 'diagnostic-level'

/** 파일명 전용 접미사 — 화면 라벨(print.*Button)과 달리 공백 없이 쓴다(i18n: print.*FileSuffix). */
const TEST_FILE_NAME_SUFFIX: Record<NamedTestPrintKind, string> = {
  chapter: '챕터테스트',
  cumulative: '누적테스트',
  'diagnostic-level': '레벨테스트',
}
/** 레벨 테스트만 학년 기반 파일명을 유지한다. 챕터·누적 테스트는 로그인 ID 기반(아래 build*TestPrintFileName). */
type GradeNamedTestPrintKind = 'diagnostic-level'

const KIND_CONFIG: Record<PrintKind, { count: number; coverTitleKey: string }> = {
  practice: { count: 5, coverTitleKey: 'print.coverTitle' },
  wrong: { count: 3, coverTitleKey: 'print.coverTitleWrong' },
  essential: { count: 5, coverTitleKey: 'print.coverTitleEssential' },
  'review-note': { count: 5, coverTitleKey: 'print.coverTitleReviewNote' },
  chapter: { count: 10, coverTitleKey: 'print.coverTitleChapter' },
  cumulative: { count: 20, coverTitleKey: 'print.coverTitleCumulative' },
  'diagnostic-level': { count: 20, coverTitleKey: 'print.coverTitleDiagnosticLevel' },
  'unit-assessment': { count: 20, coverTitleKey: 'print.coverTitleUnitAssessment' },
}

function parseKind(value: string | undefined): PrintKind {
  if (value === 'chapter_comprehensive') return 'cumulative'
  if (value === 'diagnostic-level' || value === 'unit-assessment') return value
  if (value === 'wrong' || value === 'essential' || value === 'review-note' || value === 'chapter' || value === 'cumulative') return value
  return 'practice'
}

function parsePositiveInt(value: string | undefined): number | null {
  if (!value) return null
  const n = Number(value)
  return Number.isFinite(n) && n > 0 && Number.isInteger(n) ? n : null
}

function formatPrintDate(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}.${p(d.getMonth() + 1)}.${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`
}

function diagnosticGradeLabel(levelValue: string | null | undefined, semester?: number | null): string {
  return levelSetGradeLabelKo(levelValue, semester) ?? 'grade-unknown'
}

export function buildTestPrintFileName(
  learnerName: string,
  gradeLabel: string,
  startedAt: Date,
  kind: GradeNamedTestPrintKind,
): string {
  const timestamp = formatPrintFileNameTimestamp(startedAt)
  return `${sanitizePrintFileNameSegment(learnerName, 'student')}_${sanitizePrintFileNameSegment(
    gradeLabel,
    'grade-unknown',
  )}_${timestamp}_${TEST_FILE_NAME_SUFFIX[kind]}`
}

/** `{이름}_{로그인ID}_{챕터명}_{YYMMDD_HH_mm KST}_챕터테스트` — 출제 시각은 offset 포함 ISO 문자열(`Z`/`+09:00`). */
export function buildChapterTestPrintFileName(
  learnerName: string,
  loginId: string,
  chapterTitle: string,
  generatedAt: string | Date,
  suffix = TEST_FILE_NAME_SUFFIX.chapter,
): string {
  return [
    sanitizePrintFileNameSegment(learnerName, 'student'),
    sanitizePrintFileNameSegment(loginId, 'login-id-unknown'),
    sanitizePrintFileNameSegment(chapterTitle, 'chapter-unknown'),
    formatPrintFileNameTimestampKst(generatedAt),
    sanitizePrintFileNameSegment(suffix, TEST_FILE_NAME_SUFFIX.chapter),
  ].join('_')
}

/**
 * `{이름}_{로그인ID}_{YYMMDD_HH_mm KST}_{N차}_누적테스트` (Asana 1218732821759163): 챕터 테스트와 같은 로그인 ID
 * 기준에 누적 회차를 더한다. 회차는 리포트 헤드라인 "누적 테스트 N차"와 같은 Lifetime Assessment Sequence.
 * 회차를 모르는 옛 백엔드 응답이면 회차 조각을 생략한다.
 */
export function buildCumulativeTestPrintFileName(
  learnerName: string,
  loginId: string,
  generatedAt: string | Date,
  roundLabel: string | null,
  suffix = TEST_FILE_NAME_SUFFIX.cumulative,
): string {
  return [
    sanitizePrintFileNameSegment(learnerName, 'student'),
    sanitizePrintFileNameSegment(loginId, 'login-id-unknown'),
    formatPrintFileNameTimestampKst(generatedAt),
    roundLabel ? sanitizePrintFileNameSegment(roundLabel, 'round-unknown') : null,
    sanitizePrintFileNameSegment(suffix, TEST_FILE_NAME_SUFFIX.cumulative),
  ]
    .filter((segment): segment is string => segment != null)
    .join('_')
}

export function buildDiagnosticLevelPrintFileName(
  learnerName: string,
  gradeLabel: string,
  startedAt: Date,
): string {
  return buildTestPrintFileName(learnerName, gradeLabel, startedAt, 'diagnostic-level')
}

/** 원생 없이 출력하는 레벨 테스트 세트: `{학년}_{YYMMDD_HH_mm}_레벨테스트`. */
export function buildDiagnosticSetPrintFileName(gradeLabel: string, startedAt: Date): string {
  return [
    sanitizePrintFileNameSegment(gradeLabel, 'grade-unknown'),
    formatPrintFileNameTimestamp(startedAt),
    TEST_FILE_NAME_SUFFIX['diagnostic-level'],
  ].join('_')
}

export default function CoachingPrintPage() {
  const { t } = useTranslation('coaching')
  const { learnerId: learnerIdRaw, lessonId: lessonIdRaw, kind: kindRaw } = useParams<{
    learnerId: string
    lessonId: string
    kind?: string
  }>()
  const learnerId = parsePositiveInt(learnerIdRaw)
  const lessonId = parsePositiveInt(lessonIdRaw)
  const kind = parseKind(kindRaw)

  if (learnerId === null || lessonId === null) {
    return (
      <div className="p-8 text-red-600" role="alert">
        {t('print.error.invalidParams')}
      </div>
    )
  }

  return <CoachingPrintContent learnerId={learnerId} lessonId={lessonId} kind={kind} />
}

/**
 * 콘텐츠 관리 > 진단 테스트에서 원생 없이 레벨 테스트 세트를 출력한다(Asana 1219764372903272).
 * 이름 칸은 손으로 적도록 비워 둔다.
 */
export function DiagnosticSetPrintPage() {
  const { t } = useTranslation('coaching')
  const { setId: setIdRaw } = useParams<{ setId: string }>()
  const setId = parsePositiveInt(setIdRaw)

  if (setId === null) {
    return (
      <div className="p-8 text-red-600" role="alert">
        {t('print.error.invalidParams')}
      </div>
    )
  }

  return <CoachingPrintContent learnerId={null} lessonId={setId} kind="diagnostic-level" />
}

/**
 * 측정(hidden DOM)과 실제 렌더가 같은 문제 본문을 사용하도록 분리한 문제 블록.
 * 측정 시에는 spacing=0으로 본문 높이만 재고, 실제 페이지에서는 문제 사이에만 48px을 넣는다.
 */
function WrongNoteQuizBlock({
  script,
  number,
  answerKey,
  showDescription,
  spacing,
  numberColor = QUIZ_NUMBER_COLOR,
}: {
  script: QuizScript
  number: number
  answerKey: boolean
  showDescription: boolean
  spacing: number
  numberColor?: string
}) {
  const solveAnswers = ((script.correctAnswers as unknown[] | null | undefined) ?? []).map(String)
  const embeddedAnswers = (script.answers ?? [])
    .filter((answer) => answer.correct)
    .map((answer) => String(answer.key ?? answer.answer))
  const correctAnswers = solveAnswers.length > 0 ? solveAnswers : embeddedAnswers
  return (
    <QuizNumberColorProvider value={numberColor}>
      <PrintQuizItem
        script={script}
        number={number}
        stepIdx={0}
        compact
        spacingOverride={spacing}
        showCorrectAnswers={answerKey}
        correctAnswers={correctAnswers}
        showDescription={showDescription}
      />
    </QuizNumberColorProvider>
  )
}

type CoachingPrintContentProps =
  | { learnerId: number; lessonId: number; kind: PrintKind }
  /** 원생 없는 출력은 원생 기준 쿼리를 쓰지 않는 레벨 테스트 세트만 지원한다. */
  | { learnerId: null; lessonId: number; kind: 'diagnostic-level' }

function CoachingPrintContent({ learnerId: learnerIdProp, lessonId, kind }: CoachingPrintContentProps) {
  const { t } = useTranslation('coaching')
  const [searchParams] = useSearchParams()
  const setIdParam = parsePositiveInt(searchParams.get('setId') ?? undefined)
  const isDiagnosticLevel = kind === 'diagnostic-level'
  const isBlankLearner = learnerIdProp === null
  // 원생 없는 출력(diagnostic-level)에서는 아래 원생 기준 쿼리가 모두 비활성이라 이 값이 쓰이지 않는다.
  const learnerId = learnerIdProp ?? 0
  const learnerNameParam = searchParams.get('learnerName')?.trim()
  const detail = useLearnerToday(learnerId, undefined, !isDiagnosticLevel)
  const { count, coverTitleKey } = KIND_CONFIG[kind]
  const [answerKey, setAnswerKey] = useState(false)
  const [printStartedAt] = useState(() => new Date())

  const isPractice = kind === 'practice'
  const isReviewNote = kind === 'review-note'
  const firstPageContentPadding = isReviewNote ? 32 : 40
  const quizNumberColor = isReviewNote ? '#4599ff' : QUIZ_NUMBER_COLOR
  const library = useQuery({
    queryKey: coachingKeys.library(learnerId, 'all'),
    enabled: isReviewNote,
    queryFn: () => lcCoachingApi.getLibrary(learnerId),
    staleTime: 60_000,
  })
  const printLesson = library.data?.tree
    .flatMap((curriculum) => curriculum.courses)
    .flatMap((course) => course.modules)
    .flatMap((module) => module.lessons.map((lesson, index) => ({
      ...lesson,
      number: index + 1,
    })))
    .find((lesson) => lesson.lessonId === lessonId)
  const lessonTitle = isReviewNote ? printLesson?.title : undefined
  const [lessonHeadingElement, setLessonHeadingElement] = useState<HTMLDivElement | null>(null)
  const [lessonHeadingHeight, setLessonHeadingHeight] = useState(0)
  useEffect(() => {
    const element = lessonHeadingElement
    if (!element) return
    // 헤더와 본문 여백을 합쳐 기존 첫 페이지 시작점보다 늘어난 높이만 차감한다.
    const measure = () => setLessonHeadingHeight(Math.max(0,
      element.getBoundingClientRect().height + firstPageContentPadding - LC_PRINT_FIRST_PAGE_CONTENT_TOP_PX,
    ))
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [lessonHeadingElement, firstPageContentPadding])
  const lessonQuizIds = useQuery({
    queryKey: ['lc-lesson-print-quizzes', learnerId, lessonId],
    enabled: isPractice,
    queryFn: () => lcCoachingApi.getLessonPrintQuizIds(learnerId, lessonId),
    staleTime: 60_000,
  })

  // kind='wrong': 저장된 legacy setId가 있을 때만 그 세트를 인쇄한다.
  // direct print route에서는 세트를 암묵적으로 compose하지 않는다.
  const wrongIds = useQuery({
    queryKey: ['lc-coaching-wrong-set', learnerId, lessonId, setIdParam],
    enabled: kind === 'wrong' && setIdParam != null,
    queryFn: () => lcQuizSetApi.getPrintQuizIds(learnerId, setIdParam as number),
    staleTime: 60_000,
  })

  const reviewNoteLock = useQuery({
    queryKey: ['lc-review-note-print-lock', learnerId, setIdParam],
    enabled: isReviewNote && setIdParam != null,
    queryFn: () => lcReviewNoteApi.lockForPrint(learnerId, setIdParam as number),
  })
  const reviewNoteSourceLessons = reviewNoteLock.data?.sourceLessons ?? []
  const reviewNoteSourceCounts = getReviewNoteSourceCounts(reviewNoteSourceLessons)
  const reviewNoteRangeSummary = reviewNoteSourceCounts.chapterCount > 0
    ? t('reviewNote.rangeSummary', reviewNoteSourceCounts)
    : t('reviewNote.legacyRangeSummary', reviewNoteSourceCounts)
  const sourceChapterNames = [...new Set(reviewNoteSourceLessons
    .map((lesson) => [lesson.courseTitle, lesson.chapterTitle].filter(Boolean).join(' / '))
    .filter(Boolean))]
  const visibleChapterNames = sourceChapterNames.slice(0, 3)
  const remainingChapterCount = sourceChapterNames.length - visibleChapterNames.length
  const reviewNoteSourceScope = isReviewNote && reviewNoteSourceLessons.length > 0
    ? t('reviewNote.scope.printLabel', {
        scope: [
          t(`reviewNote.source.${reviewNoteLock.data?.sourceMode ?? 'ALL'}`),
          reviewNoteRangeSummary,
          visibleChapterNames.join(' · '),
          remainingChapterCount > 0
            ? t('reviewNote.scope.moreChapters', { count: remainingChapterCount })
            : null,
        ].filter(Boolean).join(' · '),
      })
    : undefined
  const reviewNoteQuizIds = useQuery({
    queryKey: ['lc-review-note-print-quizzes', learnerId, setIdParam],
    enabled: isReviewNote && setIdParam != null && reviewNoteLock.isSuccess,
    queryFn: async () => (
      await lcReviewNoteApi.getPrintQuizIds(learnerId, setIdParam as number)
    ).quizIds,
    staleTime: 60_000,
  })

  const isEssential = kind === 'essential'
  const essentialQuizIds = useQuery({
    queryKey: ['lc-essential-print-quizzes', learnerId, lessonId],
    enabled: isEssential,
    queryFn: () => lcCoachingApi.getEssentialPrintQuizIds(learnerId, lessonId),
    staleTime: 60_000,
  })

  // Phase 5c — chapter/cumulative 는 testId 가 있으면 실 테스트 문항 quizId 로 출력(없으면 기존 더미 프리셋).
  const testIdParam = parsePositiveInt(searchParams.get('testId') ?? undefined)
  const isTestKind = kind === 'chapter' || kind === 'cumulative'
  const useRealTest = isTestKind && testIdParam != null
  const testPrintData = useQuery({
    queryKey: ['lc-test-print-quizzes', learnerId, testIdParam],
    enabled: useRealTest,
    queryFn: () => lcCoachingApi.getTestPrintQuizIds(learnerId, testIdParam as number),
    staleTime: 60_000,
  })

  // 단원평가 특강 인쇄 — uaId 의 실 문항 quizId 로 출력(반복 없음).
  const uaIdParam = parsePositiveInt(searchParams.get('uaId') ?? undefined)
  const isUnitAssessment = kind === 'unit-assessment' && uaIdParam != null
  // 단원평가 description은 교사용 YouTube 해설 URL이므로 인쇄 정답지에는 노출하지 않는다.
  const showAnswerDescription = answerKey && !isUnitAssessment
  const uaQuizIds = useQuery({
    queryKey: ['lc-ua-print-quizzes', learnerId, uaIdParam],
    enabled: isUnitAssessment,
    queryFn: () => unitAssessmentAdminApi.getPrintQuizIds(learnerId, uaIdParam as number),
    staleTime: 60_000,
  })

  // 진단 레벨테스트 인쇄: lessonId 자리에 levelQuizSetId를 전달해 기존 인쇄 캔버스를 재사용한다.
  const diagnosticLevelQuizSet = useQuery({
    queryKey: ['lc-diagnostic-level-print-quizzes', lessonId],
    enabled: isDiagnosticLevel,
    queryFn: async () => {
      const quizSet = await getQuizSet(lessonId)
      return {
        quizIds: flattenQuizIds(quizSet),
        gradeLabel: diagnosticGradeLabel(quizSet.levelValue, quizSet.semester),
      }
    },
    staleTime: 60_000,
  })

  const effectiveIds = isDiagnosticLevel
    ? (diagnosticLevelQuizSet.data?.quizIds ?? [])
    : isUnitAssessment
      ? (uaQuizIds.data ?? [])
      : isReviewNote
        ? (reviewNoteQuizIds.data ?? [])
      : kind === 'wrong'
        ? (wrongIds.data ?? [])
        : isEssential
          ? (essentialQuizIds.data ?? [])
          : useRealTest
            ? (testPrintData.data?.quizIds ?? [])
            : (lessonQuizIds.data ?? [])

  const quizzes = useQuery({
    queryKey: ['lc-coaching-print-quizzes', effectiveIds, answerKey],
    enabled: isReviewNote
      ? reviewNoteQuizIds.isSuccess && effectiveIds.length > 0
      : kind === 'wrong'
      ? wrongIds.isSuccess && effectiveIds.length > 0
      : isEssential
        ? essentialQuizIds.isSuccess && effectiveIds.length > 0
        : isDiagnosticLevel
          ? diagnosticLevelQuizSet.isSuccess && effectiveIds.length > 0
          : isUnitAssessment
            ? uaQuizIds.isSuccess && effectiveIds.length > 0
            : useRealTest
              ? testPrintData.isSuccess && effectiveIds.length > 0
              : lessonQuizIds.isSuccess && effectiveIds.length > 0,
    // 정답지는 빈칸을 {{}}로 유지하고 correctAnswers를 별도로 받아야
    // 문자열 정답({{재활용}} 등)도 문제 본문과 함께 안정적으로 렌더된다.
    queryFn: () => quizScriptApi.getQuizzes(effectiveIds, false, true, answerKey),
    staleTime: 5 * 60_000,
  })

  // useMemo로 참조를 안정화한다. 안 하면 매 렌더 새 배열 → useQuizMeasurement 재측정 →
  // setMeasuredHeights 리렌더 → 또 새 배열 → 무한 루프(브라우저/테스트 OOM).
  const cycledQuizzes = useMemo(() => {
    if (kind === 'wrong' || isReviewNote || isEssential || isDiagnosticLevel || isPractice) return quizzes.data ?? []
    if (useRealTest || isUnitAssessment) return quizzes.data ?? [] // 실 문항 그대로(반복 없음)
    return quizzes.data && quizzes.data.length > 0
      ? Array.from({ length: count }, (_, i) => quizzes.data![i % quizzes.data!.length])
      : []
  }, [kind, quizzes.data, count, useRealTest, isUnitAssessment, isDiagnosticLevel, isEssential, isPractice, isReviewNote])

  // The shared measurement hook may fall back after five seconds. Keep PDF readiness
  // separate, and start a fresh measurement when the actual page fonts have settled.
  const [printFontsSettled, setPrintFontsSettled] = useState(() => !document.fonts || document.fonts.status !== 'loading')
  useEffect(() => {
    const fonts = document.fonts
    if (!fonts) return
    let active = true
    const waitForFonts = () => {
      setPrintFontsSettled(false)
      void fonts.ready.then(
        () => { if (active) setPrintFontsSettled(true) },
        () => { if (active) setPrintFontsSettled(true) },
      )
    }
    if (fonts.status === 'loading') waitForFonts()
    fonts.addEventListener('loading', waitForFonts)
    return () => {
      active = false
      fonts.removeEventListener('loading', waitForFonts)
    }
  }, [])

  // 측정 기반 페이지 분할 (answerKey 토글 시 정답 표시로 높이가 바뀔 수 있어 deps에 포함)
  const measureRef = useRef<HTMLDivElement>(null)
  const { pages, isMeasuring } = useWrongNotePagination(
    measureRef,
    cycledQuizzes,
    [answerKey, showAnswerDescription, isDiagnosticLevel || detail.data != null, printFontsSettled],
    lessonHeadingHeight,
  )

  // Measurement starts in an effect. Require a completed cycle for these exact inputs so
  // launcher auto-download cannot capture the initial fallback pagination before images/fonts load.
  const learnerReady = isDiagnosticLevel || detail.data != null
  const paginationInput = useMemo(() => ({
    quizzes: cycledQuizzes, answerKey, showAnswerDescription, learnerReady, printFontsSettled,
  }), [cycledQuizzes, answerKey, showAnswerDescription, learnerReady, printFontsSettled])
  const measuringInput = useRef<typeof paginationInput | null>(null)
  const [measuredInput, setMeasuredInput] = useState<typeof paginationInput | null>(null)
  useEffect(() => {
    if (isMeasuring) {
      measuringInput.current = paginationInput
    } else if (measuringInput.current === paginationInput && pages.length > 0) {
      setMeasuredInput(paginationInput)
    }
  }, [isMeasuring, pages, paginationInput])

  const printRef = useRef<HTMLDivElement>(null)
  const printLearnerName = isDiagnosticLevel
    ? (learnerNameParam || t('print.learnerFallback'))
    : (detail.data?.learner.fullName || t('print.learnerFallback'))
  const testFileKind: 'chapter' | 'cumulative' = testPrintData.data?.kind === 'CHAPTER_COMPREHENSIVE'
    ? 'cumulative'
    : kind === 'cumulative'
      ? 'cumulative'
      : 'chapter'
  const cumulativeSequence = testPrintData.data?.cumulativeSequence
  const diagnosticGradeLabelText = diagnosticLevelQuizSet.data?.gradeLabel ?? 'grade-unknown'
  const documentTitle = isDiagnosticLevel
    ? isBlankLearner
      ? buildDiagnosticSetPrintFileName(diagnosticGradeLabelText, printStartedAt)
      : buildDiagnosticLevelPrintFileName(printLearnerName, diagnosticGradeLabelText, printStartedAt)
    : isTestKind && testPrintData.data
      ? testFileKind === 'chapter'
        ? buildChapterTestPrintFileName(
            printLearnerName,
            testPrintData.data.loginId,
            testPrintData.data.scopeTitle,
            testPrintData.data.generatedAt,
            t('print.chapterFileSuffix'),
          )
        : buildCumulativeTestPrintFileName(
            printLearnerName,
            testPrintData.data.loginId,
            testPrintData.data.generatedAt,
            cumulativeSequence != null ? t('print.cumulativeFileRound', { n: cumulativeSequence }) : null,
            t('print.cumulativeFileSuffix'),
          )
      : isReviewNote && reviewNoteLock.data
        ? [
            sanitizePrintFileNameSegment(printLearnerName, 'student'),
            sanitizePrintFileNameSegment(lessonTitle ?? '', 'lesson'),
            sanitizePrintFileNameSegment(t(`reviewNote.source.${reviewNoteLock.data.sourceMode}`), 'questions'),
            formatPrintFileNameTimestampKst(printStartedAt),
            sanitizePrintFileNameSegment(t('print.reviewNoteFileSuffix'), 'ReviewNote'),
          ].join('_')
        : undefined

  const isPrintDataLoading = quizzes.isLoading ||
    (isReviewNote && (library.isLoading || reviewNoteLock.isLoading || reviewNoteQuizIds.isLoading)) ||
    (kind === 'wrong' && wrongIds.isLoading) ||
    (isPractice && lessonQuizIds.isLoading) ||
    (isEssential && essentialQuizIds.isLoading) ||
    (isDiagnosticLevel && diagnosticLevelQuizSet.isLoading) ||
    (useRealTest && testPrintData.isLoading) ||
    (isUnitAssessment && uaQuizIds.isLoading)
  const printDataError = quizzes.error ||
    (isReviewNote && (library.error || (library.isSuccess && !printLesson) || reviewNoteQuizIds.error)) ||
    (kind === 'wrong' && wrongIds.error) ||
    (isPractice && lessonQuizIds.error) ||
    (isEssential && essentialQuizIds.error) ||
    (isDiagnosticLevel && diagnosticLevelQuizSet.error) ||
    (useRealTest && testPrintData.error) ||
    (isUnitAssessment && uaQuizIds.error)
  const canPrint =
    !isPrintDataLoading && !printDataError && printFontsSettled && cycledQuizzes.length > 0 && !isMeasuring && pages.length > 0 && measuredInput === paginationInput
  const { print: reactToPrintFn, downloadPdf, canDownloadPdf, isDownloading, error: pdfError } = useLmsPrint({
    contentRef: printRef,
    canPrint,
    documentTitle,
    pageStyle: `
      @page { size: A4 portrait; margin: 0; }
      @media print {
        html, body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .print-page { page-break-after: always; break-after: page; }
        .print-page:last-child { page-break-after: auto; break-after: auto; }
        .break-inside-avoid { page-break-inside: avoid; break-inside: avoid; }
        .print-action-bar { display: none !important; }
      }
    `,
  })

  if (isReviewNote && setIdParam == null) {
    return (
      <div className="p-8 text-red-600" role="alert">
        {t('print.error.missingReviewNoteSet')}
      </div>
    )
  }
  if (kind === 'wrong' && setIdParam == null) {
    return (
      <div className="p-8 text-red-600" role="alert">
        {t('print.error.missingWrongSet')}
      </div>
    )
  }
  if (isReviewNote && reviewNoteLock.error) {
    return (
      <div className="p-8" role="alert">
        <p className="text-red-600">{t('print.error.reviewNoteLock')}</p>
        <button
          type="button"
          onClick={() => reviewNoteLock.refetch()}
          disabled={reviewNoteLock.isFetching}
          className="mt-4 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 disabled:opacity-50"
        >
          {t('print.retry')}
        </button>
      </div>
    )
  }

  if (!isDiagnosticLevel && detail.isLoading) {
    return (
      <div className="p-8" role="status" aria-live="polite">
        {t('print.loading')}
      </div>
    )
  }
  if (!isDiagnosticLevel && (detail.error || !detail.data)) {
    return (
      <div className="p-8 text-red-600" role="alert">
        {t('print.error.loadLearner')}
      </div>
    )
  }

  const learnerName = isBlankLearner
    ? ''
    : isDiagnosticLevel
      ? (learnerNameParam || t('print.learnerFallback'))
      : detail.data!.learner.fullName
  const headerTitle = answerKey
    ? t('print.coverTitleAnswerKey')
    : kind === 'wrong'
      ? t('print.wrongNoteTitle')
      : t(coverTitleKey)
  const dateText = isReviewNote
    ? new Intl.DateTimeFormat('sv-SE', {
        timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit',
      }).format(printStartedAt).replace(/-/g, '.')
    : formatPrintDate(printStartedAt)

  return (
    <div className="min-h-screen bg-gray-100">
      <div
        data-testid="coaching-print-actions"
        className="print-action-bar sticky top-0 z-10 flex flex-wrap gap-2 border-b border-gray-200 bg-white px-6 py-3"
      >
        <button
          type="button"
          onClick={() => reactToPrintFn()}
          disabled={!canPrint}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Printer size={16} />
          {t(isDiagnosticLevel ? 'print.diagnosticPrintAction' : 'print.printAction')}
        </button>
        {canDownloadPdf && (
          <LmsPdfDownloadButton onClick={downloadPdf} disabled={!canPrint} isDownloading={isDownloading} error={pdfError} />
        )}
        <button
          type="button"
          aria-pressed={answerKey}
          onClick={() => setAnswerKey((v) => !v)}
          className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
        >
          {answerKey ? t('print.studentToggle') : t('print.answerKeyToggle')}
        </button>
      </div>

      {/* 측정용 hidden DOM — 실제 인쇄 본문 폭(794 − 72*2 = 650)으로 간격을 제외한
          문제 본문 높이만 잰다. printRef 밖에 두어 인쇄물에는 포함되지 않는다. */}
      <div
        ref={measureRef}
        aria-hidden
        style={{
          position: 'absolute',
          left: -9999,
          top: 0,
          width: LC_PRINT_A4_WIDTH_PX - 72 * 2,
          visibility: 'hidden',
        }}
      >
        {cycledQuizzes.map((script, i) => (
          <div key={`measure-${i}`} data-quiz-measure>
            <WrongNoteQuizBlock
              script={script}
              number={i + 1}
              numberColor={quizNumberColor}
              answerKey={answerKey}
              showDescription={showAnswerDescription}
              spacing={0}
            />
          </div>
        ))}
      </div>

      <div aria-hidden className="pointer-events-none fixed left-[-10000px] top-0" style={{ width: LC_PRINT_A4_WIDTH_PX }}>
        <div ref={setLessonHeadingElement} data-lesson-heading-measure>
          {lessonTitle && <WrongNotePrintHeader title={headerTitle} learnerName={learnerName} dateText={dateText} lessonTitle={lessonTitle} lessonNumber={printLesson?.number} sourceScope={reviewNoteSourceScope} />}
        </div>
      </div>
      <div className="mx-auto py-8" style={{ maxWidth: LC_PRINT_A4_WIDTH_PX }}>
        {isPrintDataLoading && (
          <div className="bg-white p-16" role="status" aria-live="polite">
            {t('print.loading')}
          </div>
        )}
        {printDataError && (
          <div className="bg-white p-16 text-red-600" role="alert">
            {t('print.error.fetch')}
          </div>
        )}
        {pages.length > 0 && (
          <div ref={printRef} className="print-container">
            {pages.map((page) => (
              <div
                key={page.pageIndex}
                className="print-page relative mb-8 bg-white shadow-lg print:mb-0 print:shadow-none"
                style={{ height: LC_PRINT_A4_HEIGHT_PX, overflow: 'hidden' }}
              >
                {page.pageIndex === 0 && (
                  <WrongNotePrintHeader
                    title={headerTitle}
                    learnerName={learnerName}
                    dateText={dateText}
                    lessonTitle={lessonTitle}
                    lessonNumber={isReviewNote ? printLesson?.number : undefined}
                    sourceScope={reviewNoteSourceScope}
                  />
                )}
                <div
                  data-testid="wrong-note-page-content"
                  className="px-[72px]"
                  style={{ paddingTop: page.pageIndex === 0 ? firstPageContentPadding : WRONG_NOTE_CONTINUATION_TOP_PX }}
                >
                  {page.items.map((script, idx) => (
                    <div key={`${page.pageIndex}-${idx}`} className="break-inside-avoid">
                      <WrongNoteQuizBlock
                        script={script}
                        number={page.startNumber + idx}
                        numberColor={quizNumberColor}
                        answerKey={answerKey}
                        showDescription={showAnswerDescription}
                        spacing={idx === page.items.length - 1 ? 0 : LC_PRINT_QUIZ_SPACING_PX}
                      />
                    </div>
                  ))}
                </div>
                <WrongNotePrintFooter pageNumber={page.pageIndex + 1} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
